import { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import useAnimatedPresence from '../../hooks/useAnimatedPresence';
import { Z } from '../../constants/zIndex';
import {
  getDropdownMenuWidthPresetClassName,
  getDropdownWidthPresetClassName,
  getDropdownWidthPresetMinWidth,
  getDropdownMenuGap,
  getFallbackDropdownSurfaceHeight,
  getFallbackDropdownSurfaceWidth,
  readCssPixelValue,
  resolveDropdownWidthPreset,
} from './dropdownWidth';

// Visible viewport, excluding the scrollbar — clamping against window.innerWidth leaves the
// menu hanging past the visible edge by the scrollbar width. clientWidth reports 0 without
// layout (jsdom), so fall back to the window size there.
const viewportWidth = () => document.documentElement.clientWidth || window.innerWidth;

// HOW MUCH ROOM THE MENU ACTUALLY NEEDS.
//
// This was a flat 180px, on the reasoning that the menu is not laid out when the decision
// runs so it cannot be measured. The constant was below the menu's own ceiling -- compact
// menus are allowed 260px and default ones 300px -- so between 180px and that ceiling the
// menu stayed below its trigger and got cut off by the bottom of the viewport. The options
// were in the DOM and focusable, which is why every behavioural assertion passed.
//
// Raising the constant would not fix it. Max-height is a token James can retune live (up to
// 480px through the design-system registry), so any constant here is a second copy of a fact
// the tokens own, and a bigger one also flips SHORT menus that fit perfectly well.
//
// So the decision is derived at render from the measured menu instead. The measurement runs
// in a layout effect, whose state update React flushes BEFORE the browser paints, so the
// token-derived estimate below is only ever a first-render seed -- the user never sees a
// placement chosen from it. That is what the old comment's objection was really about, and
// layout-effect timing answers it.

import {
  DEFAULT_FLOATING_DISMISS_GROUP,
  useFloatingSurfaceDismissal,
} from './floatingSurfaceDismissal';

const VIEWPORT_PADDING = 8;

function getMeasuredMenuWidth(menuElement, widthPreset, minWidth) {
  if (!menuElement) return getFallbackDropdownSurfaceWidth(widthPreset, minWidth);

  const layoutWidth = menuElement.offsetWidth
    || menuElement.scrollWidth
    || menuElement.getBoundingClientRect().width
    || 0;
  if (Number.isFinite(layoutWidth) && layoutWidth > 0) {
    return layoutWidth;
  }

  const computedStyle = window.getComputedStyle?.(menuElement);
  if (!computedStyle) return getFallbackDropdownSurfaceWidth(widthPreset, minWidth);

  const explicitMinWidth = readCssPixelValue(minWidth);
  const maxWidth = readCssPixelValue(computedStyle.maxWidth);
  const computedMinWidth = readCssPixelValue(computedStyle.minWidth);
  return explicitMinWidth || maxWidth || computedMinWidth || getFallbackDropdownSurfaceWidth(widthPreset, minWidth);
}

/**
 * The menu's real laid-out height, or the token estimate until there is one.
 *
 * Same immunity ordering as the width above, and it matters more here: the open animation
 * is a scale (`fadeInScale`), and `getBoundingClientRect` reports the SCALED box mid-flight
 * while `offsetHeight` reports the layout box regardless of transform. Measuring the scaled
 * box during the animation would under-report the height and un-flip a menu that had
 * correctly flipped.
 */
function getMeasuredMenuHeight(menuElement, size, maxHeight) {
  if (!menuElement) return getFallbackDropdownSurfaceHeight(size, maxHeight);

  const layoutHeight = menuElement.offsetHeight
    || menuElement.scrollHeight
    || menuElement.getBoundingClientRect().height
    || 0;
  if (Number.isFinite(layoutHeight) && layoutHeight > 0) {
    return layoutHeight;
  }
  return getFallbackDropdownSurfaceHeight(size, maxHeight);
}

function getClampedLeft({
  align,
  menuWidth,
  metrics,
  viewportInsetLeft,
  viewportInsetRight,
}) {
  if (!metrics || !Number.isFinite(menuWidth) || menuWidth <= 0) {
    return null;
  }

  let baseLeft = metrics.triggerLeft;

  if (align === 'center') {
    baseLeft = metrics.triggerCenter - menuWidth / 2;
  } else if (align === 'right') {
    baseLeft = metrics.triggerRight - menuWidth;
  }

  const maxLeft = Math.max(
    viewportInsetLeft,
    viewportWidth() - viewportInsetRight - menuWidth,
  );
  return Math.min(Math.max(baseLeft, viewportInsetLeft), maxLeft);
}

/**
 * DropdownMenu - Floating dropdown container for canvas HUD menus
 * Used in: TidyUpDropdown, GenerativeActionsDropdown, MarqueeActionDialog group dropdown
 *
 * Renders via portal to document.body so backdrop-filter works independently
 * of parent stacking contexts (e.g. HUD panels with their own blur).
 *
 * @param {boolean} open - Whether the dropdown is visible
 * @param {function} onClose - Called when dropdown should close (click outside, etc.)
 * @param {ReactNode} children - Menu content (typically MenuItem components)
 * @param {string} align - Horizontal alignment: 'left' | 'center' | 'right' (default: 'center')
 * @param {boolean} matchTriggerWidth - Whether menu min-width should be at least the trigger width
 * @param {string} minWidth - Override minimum width (defaults to CSS variable per size tier)
 * @param {string} maxHeight - Override maximum height (defaults to CSS variable per size tier)
 * @param {'compact'|'default'} size - Menu sizing tier (default: 'compact')
 * @param {'tiny'|'compact'|'default'} widthPreset - Semantic width bucket. Defaults to size.
 * @param {string|null} dismissGroup - Mutual-dismissal group. Pass null to opt out.
 * @param {string} className - Additional CSS classes
 * @param {object} style - Additional inline styles
 */
const DropdownMenu = ({
  open,
  onClose,
  children,
  align = 'center',
  position = 'below',
  positionAnchorRef = null,
  matchTriggerWidth = false,
  minWidth,
  maxHeight,
  viewportInsetLeft = VIEWPORT_PADDING,
  viewportInsetRight = VIEWPORT_PADDING,
  size = 'compact',
  widthPreset,
  className = '',
  style = {},
  portalRoot = null,
  outsideCloseEvent = 'mousedown',
  dismissGroup = DEFAULT_FLOATING_DISMISS_GROUP,
  onSurfacePointerLeave = null,
  onSurfaceFocusLeave = null,
}) => {
  const { mounted, stage, ref: animRef } = useAnimatedPresence(open);
  const anchorRef = useRef(null);
  const menuRef = useRef(null);
  const onCloseRef = useRef(onClose);
  useLayoutEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  const [metrics, setMetrics] = useState(null);
  const [menuWidth, setMenuWidth] = useState(null);
  const [menuHeight, setMenuHeight] = useState(null);
  const resolvedWidthPreset = resolveDropdownWidthPreset(widthPreset, size);

  const setRefs = useCallback((el) => {
    menuRef.current = el;
    animRef.current = el;
  }, [animRef]);

  // Position relative to either a custom anchor or the trigger wrapper
  useLayoutEffect(() => {
    if (!open || !anchorRef.current) {
      return undefined;
    }

    const positionAnchor = positionAnchorRef?.current ?? anchorRef.current.parentElement;
    if (!positionAnchor) {
      return undefined;
    }

    // Facts only. Which side the menu lands on is DERIVED at render, because it depends on
    // the menu's measured height and this effect runs before the menu exists to measure.
    // Deciding here as well would give that one question two owners answering from
    // different information, and the stale one runs first.
    const updateMetrics = () => {
      const rect = positionAnchor.getBoundingClientRect();

      setMetrics({
        triggerTop: rect.top,
        triggerBottom: rect.bottom,
        triggerLeft: rect.left,
        triggerCenter: rect.left + rect.width / 2,
        triggerRight: rect.right,
        triggerWidth: rect.width,
      });
    };

    updateMetrics();
    window.addEventListener('resize', updateMetrics);
    window.addEventListener('scroll', updateMetrics, true);

    return () => {
      window.removeEventListener('resize', updateMetrics);
      window.removeEventListener('scroll', updateMetrics, true);
    };
  }, [open, align, position, positionAnchorRef]);

  useLayoutEffect(() => {
    if (!open || !mounted || !metrics || !menuRef.current) {
      return undefined;
    }

    const measureSurface = () => {
      const width = getMeasuredMenuWidth(menuRef.current, resolvedWidthPreset, minWidth);
      if (Number.isFinite(width) && width > 0) {
        setMenuWidth((previous) => (previous === width ? previous : width));
      }

      const height = getMeasuredMenuHeight(menuRef.current, size, maxHeight);
      if (Number.isFinite(height) && height > 0) {
        setMenuHeight((previous) => (previous === height ? previous : height));
      }
    };

    const timeoutId = window.setTimeout(measureSurface, 0);
    measureSurface();

    let observer = null;
    if (typeof ResizeObserver === 'function') {
      observer = new ResizeObserver(() => {
        measureSurface();
      });
      observer.observe(menuRef.current);
    }

    return () => {
      window.clearTimeout(timeoutId);
      observer?.disconnect();
    };
  }, [open, mounted, metrics, children, minWidth, maxHeight, resolvedWidthPreset, size]);

  // Click-outside-to-close
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e) => {
      const triggerWrapper = positionAnchorRef?.current ?? anchorRef.current?.parentElement;
      const clickedMenu = menuRef.current?.contains(e.target);
      const clickedTrigger = triggerWrapper?.contains(e.target);
      if (clickedMenu || clickedTrigger) return;
      onCloseRef.current?.();
    };

    const timer = setTimeout(() => {
      window.addEventListener(outsideCloseEvent, handleClickOutside, true);
    }, 0);

    return () => {
      clearTimeout(timer);
      window.removeEventListener(outsideCloseEvent, handleClickOutside, true);
    };
  }, [open, positionAnchorRef, outsideCloseEvent]);

  // Escape closes this menu only when the shared floating owner grants it the key: a nested
  // editor's claim wins, and otherwise only the innermost/topmost open surface closes.
  useFloatingSurfaceDismissal({
    open,
    dismissGroup,
    onDismiss: onClose,
    onEscape: () => onCloseRef.current?.(),
    surfaceRef: menuRef,
    contextRef: anchorRef,
  });

  // FLIP WHEN THE REQUESTED SIDE DOES NOT FIT.
  //
  // `position` used to be obeyed absolutely, so a menu opened low in a panel was simply
  // clipped by the bottom of the viewport. That is a property of where the trigger happens
  // to sit, not of the consumer, so it belongs here rather than in every panel that might
  // one day be scrolled near an edge.
  //
  // Derived, not stored: it depends on the measured menu height, which only exists after
  // the menu mounts. The requested side still WINS whenever it fits, and when NEITHER side
  // fits the requested side keeps it -- flipping into a space that is also too small just
  // moves the clipping.
  const neededMenuSpace = (menuHeight || getFallbackDropdownSurfaceHeight(size, maxHeight))
    + getDropdownMenuGap()
    + VIEWPORT_PADDING;
  const requestedAbove = position === 'above';
  const spaceBelow = metrics ? window.innerHeight - metrics.triggerBottom : 0;
  const spaceAbove = metrics ? metrics.triggerTop : 0;
  const requestedSpace = requestedAbove ? spaceAbove : spaceBelow;
  const oppositeSpace = requestedAbove ? spaceBelow : spaceAbove;
  const resolvedAbove = metrics && requestedSpace < neededMenuSpace && oppositeSpace > requestedSpace
    ? !requestedAbove
    : requestedAbove;

  const menuClasses = [
    'ui-floating-surface',
    'ui-floating-surface--dropdown',
    getDropdownWidthPresetClassName(resolvedWidthPreset),
    'dropdown-menu',
    size === 'compact' && 'dropdown-menu--compact',
    getDropdownMenuWidthPresetClassName(resolvedWidthPreset),
    resolvedAbove ? 'dropdown-menu--portal-above' : 'dropdown-menu--portal-below',
    className
  ].filter(Boolean).join(' ');

  const positioningMenuWidth = menuWidth || getFallbackDropdownSurfaceWidth(resolvedWidthPreset, minWidth);
  const resolvedLeft = getClampedLeft({
    align,
    menuWidth: positioningMenuWidth,
    metrics,
    viewportInsetLeft,
    viewportInsetRight,
  });

  const positionStyles = metrics ? {
    position: 'fixed',
    ...(resolvedAbove
      ? { bottom: `${window.innerHeight - metrics.triggerTop}px` }
      : { top: `${metrics.triggerBottom}px` }),
    ...(resolvedLeft !== null
      ? {
          left: `${resolvedLeft}px`,
          right: 'auto',
          transform: 'none',
        }
      : align === 'center'
        ? {
            left: `${metrics.triggerCenter}px`,
            translate: '-50% 0',
          }
        : align === 'right'
          ? {
              right: `${viewportWidth() - metrics.triggerRight}px`,
            }
          : {
              left: `${metrics.triggerLeft}px`,
            }),
  } : null;

  const resolvedMinWidth = matchTriggerWidth && metrics
    ? `max(${minWidth || getDropdownWidthPresetMinWidth(resolvedWidthPreset)}, ${metrics.triggerWidth}px)`
    : minWidth;

  const isExiting = stage === 'exiting';
  const isPositionResolved = resolvedLeft !== null;
  const enterAnim = 'fadeInScale 150ms ease-out';
  const exitAnim = 'fadeOutScale 150ms ease-in forwards';

  return (
    <>
      <span ref={anchorRef} style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }} />
      {mounted && metrics && createPortal(
        <div
          ref={setRefs}
          className={menuClasses}
          onPointerLeave={onSurfacePointerLeave || undefined}
          onBlur={onSurfaceFocusLeave || undefined}
          style={{
            ...positionStyles,
            zIndex: Z.CANVAS_DROPDOWN,
            animation: isExiting ? exitAnim : enterAnim,
            pointerEvents: isExiting || !isPositionResolved ? 'none' : undefined,
            ...(resolvedMinWidth && { minWidth: resolvedMinWidth }),
            ...(maxHeight && { maxHeight }),
            ...style,
            ...(!isPositionResolved && {
              visibility: 'hidden',
              pointerEvents: 'none',
            }),
          }}
        >
          {children}
        </div>,
        portalRoot || document.body
      )}
    </>
  );
};

/**
 * MenuDivider - Horizontal separator for dropdown menus
 */
export const MenuDivider = () => (
  <div style={{
    height: '1px',
    backgroundColor: 'var(--border-primary)',
    margin: 'var(--space-xs) 0',
  }} />
);

export default DropdownMenu;
